import { isFailedCrossChainStatus } from '@/lib/api/0x-cross-chain/status';

import { CompletedTransactionView } from '@/components/TransactionModule/TransactionStatus/CompletedTransaction';
import { FailedTransactionView } from '@/components/TransactionModule/TransactionStatus/FailedTransaction';
import { PendingTransactionView } from '@/components/TransactionModule/TransactionStatus/PendingTransaction';
import { SubmittedOrderView } from '@/components/TransactionModule/TransactionStatus/SubmittedOrder';

import { useCheckoutMode } from '@/hooks/useCheckoutMode';

import { useTransactionStore } from '@/store/transaction';
import { isTransactionPending, isTransactionSuccessful } from '@/store/transaction/utils';

import { useCheckout } from '../../useCheckout';
import { useDismissStatusScreen } from '../../useDismissStatusScreen';

/** Routes rewrite checkout and transaction records to their status presentation. */
export const StatusScreen = () => {
  const [{ step }, dispatchCheckout] = useCheckout();
  const dismissScreen = useDismissStatusScreen();
  const resetCheckout = () => dispatchCheckout({ type: 'reset' });
  const [checkoutMode] = useCheckoutMode();
  const currentTransaction = useTransactionStore((state) => state.currentTransaction);
  const transactions = useTransactionStore((state) => state.transactions);

  if (step === 'submitFailed') return <FailedTransactionView onBack={resetCheckout} />;
  if (step !== 'submitted') return null;
  if (checkoutMode === 'advanced') return <SubmittedOrderView onReset={resetCheckout} />;
  if (!currentTransaction) return null;

  const currentHash = currentTransaction.tx.hash;
  if (!currentHash) return null;
  const transaction = transactions[currentTransaction.address]?.[currentTransaction.chainId]?.find(
    (candidate) => candidate.hash === currentHash || candidate.originalHash === currentHash,
  );

  if (!transaction) return null;
  if (
    transaction.transactionType === 'cross-chain' &&
    (transaction.failureContext || isFailedCrossChainStatus(transaction.tradeStatus?.status))
  ) {
    return <FailedTransactionView onBack={resetCheckout} />;
  }

  // A cross-chain trade succeeds on the origin chain before the bridge delivers.
  const isAwaitingDestination =
    transaction.transactionType === 'cross-chain' && !transaction.destinationTxHash;
  if (
    isTransactionPending(transaction) ||
    (isTransactionSuccessful(transaction) && isAwaitingDestination)
  ) {
    return <PendingTransactionView transaction={transaction} onDismiss={dismissScreen} />;
  }

  if (isTransactionSuccessful(transaction)) {
    return <CompletedTransactionView onDone={resetCheckout} />;
  }

  return <FailedTransactionView onBack={resetCheckout} />;
};
